import type { IDocument } from '@lib-shared/types/Document';
import type { IDocumentPage } from '@lib-shared/types/document-page';
import type { IEditorRecipientInput } from '@lib-shared/types/editor-recipient-input';
import {
  EBlockWidth,
  EBreakpoint,
  EDividerPadding,
  EDividerType,
  EImageAlign,
  EImageSize,
  ENodeType,
  FieldInputType,
} from '../types/enums';
import { fallbackTableContent, fallbackTextContent } from '../constants';
import type {
  ICell,
  IRow,
  IBlockWidth,
  IImage,
  IField,
  Form,
  IText,
  ITable,
  ITableStyle,
  INode,
  IPricingTable,
  IPricingTableSummaryValues,
  IPricingTableCurrencySettings,
  INodePayload,
  TextInputSettings,
  DropdownInputSettings,
} from '../types';
import { ProsemirrorHelper } from './prosemirror.helper';

export class EditorHelper {
  static readonly NANO_ID_LENGTH = 21;
  static readonly mergeTagExpression = `{{.{${EditorHelper.NANO_ID_LENGTH}}}}`;

  static hasHiddenContentRight({ offsetWidth, scrollWidth, scrollLeft }: HTMLElement): boolean {
    if (scrollWidth <= offsetWidth) {
      return false;
    }

    return offsetWidth + scrollLeft !== scrollWidth;
  }

  static isBlockWidth(size: EBlockWidth, width: number): boolean {
    switch (size) {
      case EBlockWidth.XXSmall:
        return width <= EBreakpoint.XXSmall;
      case EBlockWidth.XSmall:
        return width <= EBreakpoint.XSmall;
      case EBlockWidth.Small:
        return width <= EBreakpoint.Small;
      case EBlockWidth.Medium:
        return width <= EBreakpoint.Medium;
      case EBlockWidth.Large:
        return width <= EBreakpoint.Large;
      case EBlockWidth.XLarge:
        return width > EBreakpoint.Large;
    }
  }

  static getBlockWidth(width: number): IBlockWidth {
    return {
      xxs: EditorHelper.isBlockWidth(EBlockWidth.XXSmall, width),
      xs: EditorHelper.isBlockWidth(EBlockWidth.XSmall, width),
      sm: EditorHelper.isBlockWidth(EBlockWidth.Small, width),
      md: EditorHelper.isBlockWidth(EBlockWidth.Medium, width),
      lg: EditorHelper.isBlockWidth(EBlockWidth.Large, width),
      xl: EditorHelper.isBlockWidth(EBlockWidth.XLarge, width),
    };
  }

  static getBlockWidthClass(blockWidth: IBlockWidth): EBlockWidth {
    if (blockWidth?.xxs) {
      return EBlockWidth.XXSmall;
    } else if (blockWidth?.xs) {
      return EBlockWidth.XSmall;
    } else if (blockWidth?.sm) {
      return EBlockWidth.Small;
    } else if (blockWidth?.md) {
      return EBlockWidth.Medium;
    } else if (blockWidth?.lg) {
      return EBlockWidth.Large;
    } else if (blockWidth?.xl) {
      return EBlockWidth.XLarge;
    } else {
      return EBlockWidth.XXSmall;
    }
  }

  static rowContainsCoverImage({ cells }: IRow): boolean {
    const imageCoverNode = (node: IImage) =>
      node.type === ENodeType.Image && node.imageSize === EImageSize.Cover;

    return !!cells.find(({ nodes }: ICell) => !!nodes.find(imageCoverNode));
  }

  static rowContainsLockedCells(row: IRow): boolean {
    if (!row) {
      return false;
    }
    return !!row.cells.find(({ nodes }: ICell) => !!nodes.find(node => node.locked));
  }

  static getEditorInputFieldsWithPageNumInPage({
    editorBlock,
    pageNum,
  }: IDocumentPage): (IField & { pageNum: number })[] {
    if (!editorBlock) {
      return [];
    }
    return editorBlock.content.sections.flatMap(({ rows }) =>
      rows.flatMap(({ cells }) =>
        cells.flatMap(({ nodes }) =>
          nodes
            .filter(node => node.type === ENodeType.Form)
            .flatMap(({ inputFieldSets, fields }: Form) =>
              inputFieldSets
                .flatMap(fieldSet => fieldSet.ids)
                .map(id => ({
                  ...fields.find(field => field.id === id),
                  pageNum,
                }))
            )
        )
      )
    );
  }

  static getEditorInputFieldsWithPageNumInDocument({
    pages,
  }: IDocument): (IField & { pageNum: number })[] {
    return pages
      .filter(page => page.editorBlock)
      .flatMap(EditorHelper.getEditorInputFieldsWithPageNumInPage);
  }

  static getRequiredFields(document: IDocument): (IField & { pageNum: number })[] {
    return EditorHelper.getEditorInputFieldsWithPageNumInDocument(document).filter(
      field => field.inputSettings.required
    );
  }

  static getEmptyFormFieldsAssignedToRecipient(
    fields: (IField & { pageNum: number })[],
    recipientInputs: IEditorRecipientInput[],
    currentRecipientId: string
  ) {
    return fields.filter(({ id, recipientId, inputSettings, value: contentValue }) => {
      const fieldInput = recipientInputs.find(recipientInput => recipientInput.fieldId === id);
      const assignedToCurrentRecipient = currentRecipientId === recipientId;
      const isTextInputWithoutValue =
        inputSettings.type === FieldInputType.Text &&
        !(fieldInput?.value || inputSettings.defaultValue);
      const isUncheckedCheckboxInput =
        inputSettings.type === FieldInputType.Checkbox &&
        !(fieldInput?.value === '1' || contentValue === '1');
      const isUnselectedDropdownInput =
        inputSettings.type === FieldInputType.Dropdown && !(fieldInput?.value || contentValue);

      return (
        assignedToCurrentRecipient &&
        (isTextInputWithoutValue || isUncheckedCheckboxInput || isUnselectedDropdownInput)
      );
    });
  }

  static IsJsonString(str: string) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }

  private static getNewTextNode({
    content,
    fields,
    nodeType: type,
    nodeId: id,
    locked,
  }: INodePayload): IText {
    return {
      id,
      type,
      content: content || fallbackTextContent,
      fields: fields || [],
      locked: locked || false,
    };
  }

  private static getNewImageNode({
    nodeType: type,
    nodeId: id,
    imageId,
    srcUrl,
    linkUrl,
    imageSize,
    caption,
    heading,
    imageAlign,
    imageTransforms,
    attribution,
    locked,
  }: INodePayload): IImage {
    return {
      id,
      type,
      imageId: imageId || '',
      srcUrl: srcUrl || '',
      linkUrl: linkUrl || '',
      imageSize: imageSize || EImageSize.Original,
      caption: caption || '',
      heading: heading || '',
      imageAlign: imageAlign || EImageAlign.Center,
      imageTransforms: imageTransforms || null,
      attribution: attribution || null,
      locked: locked || false,
    };
  }

  private static getNewTableNode({
    nodeType: type,
    nodeId: id,
    content,
    tableStyle,
    fields,
    locked,
  }: INodePayload): ITable {
    return {
      id,
      type,
      content: content || fallbackTableContent,
      tableStyle: tableStyle || ({} as ITableStyle),
      fields: fields || [],
      locked: locked || false,
    };
  }

  private static getNewDividerNode({
    nodeType: type,
    nodeId: id,
    dividerType,
    dividerPadding,
    locked,
  }: INodePayload): INode {
    return {
      id,
      type,
      dividerType: dividerType || EDividerType.Thin,
      dividerPadding: dividerPadding || EDividerPadding.None,
      locked: locked || false,
    };
  }

  private static getNewFormNode({
    nodeType: type,
    nodeId: id,
    inputFieldSets,
    fields,
    locked,
  }: INodePayload): Form {
    return {
      id,
      type,
      inputFieldSets: inputFieldSets || [],
      fields: fields || [],
      locked: locked || false,
    };
  }

  private static getNewPricingTableNode({
    nodeType: type,
    nodeId: id,
    name,
    pricingTableSections,
    fields,
    content,
    locked,
    pricingTableSummaryValues,
    currencySettings,
  }: INodePayload): IPricingTable {
    return {
      id,
      type,
      name: name || '',
      pricingTableSections: pricingTableSections || [],
      preCalculated: false,
      fields: fields || [],
      content: content || '',
      locked: locked || false,
      pricingTableSummaryValues: pricingTableSummaryValues || ({} as IPricingTableSummaryValues),
      currencySettings: currencySettings || ({} as IPricingTableCurrencySettings),
    };
  }

  static getNewNode(payload: INodePayload): INode | undefined {
    switch (payload.nodeType) {
      case ENodeType.Text:
        return EditorHelper.getNewTextNode(payload);

      case ENodeType.Image:
        return EditorHelper.getNewImageNode(payload);

      case ENodeType.Table:
        return EditorHelper.getNewTableNode(payload);

      case ENodeType.Divider:
        return EditorHelper.getNewDividerNode(payload);

      case ENodeType.Form:
        return EditorHelper.getNewFormNode(payload);

      case ENodeType.PricingTable:
        return EditorHelper.getNewPricingTableNode(payload);

      default:
        return undefined;
    }
  }

  static getTextValue(
    input: IField & { inputSettings: TextInputSettings },
    recipientInput: IEditorRecipientInput[]
  ): string {
    if (input.userId) {
      return input.value || '';
    }

    const fieldValue = recipientInput?.find(field => field.fieldId === input.id)?.value;

    if (fieldValue || fieldValue === '') {
      return fieldValue;
    }

    return input.inputSettings.defaultValue || '';
  }

  static getCheckboxValue(input: IField, recipientInput: IEditorRecipientInput[]): boolean {
    if (input.userId) {
      return input.value === '1';
    }

    const fieldValue = recipientInput?.find(field => field.fieldId === input.id)?.value;

    if (fieldValue) {
      return fieldValue === '1';
    }

    return input.value === '1';
  }

  static getDropdownValue(input: IField, recipientInput: IEditorRecipientInput[]): string {
    if (input.userId) {
      return input.value || '';
    }

    const fieldValue = recipientInput?.find(field => field.fieldId === input.id)?.value;

    return fieldValue || input.value || '';
  }

  static getDropdownValueContent(
    input: IField & { inputSettings: DropdownInputSettings },
    fields: IField[],
    recipientInput: IEditorRecipientInput[]
  ) {
    return (
      ProsemirrorHelper.getInputContent(
        EditorHelper.getDropdownValue(input, recipientInput),
        fields
      ) || ProsemirrorHelper.getInputContent(input.inputSettings.placeholder, fields)
    );
  }
}
